import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-8">
          {/* Logo */}
          <div className="text-center space-y-2">
            <h1 className="text-[#0B3D2E]">
              <span className="text-sm font-light tracking-widest">The</span>
              <span className="block text-4xl font-brand tracking-wide">TRIBE</span>
              <span className="text-sm font-light tracking-widest">App</span>
            </h1>
            <p className="text-[#4A5D56] text-xs tracking-wide mt-4">
              Home of the Hauora Tribe, NZ since 2020
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#0B3D2E]/5">
            <h2 className="text-2xl font-brand text-[#051F17] mb-8 text-center">
              Kia ora!
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#4A5D56] text-sm font-medium">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8FA39B]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 h-14 bg-[#F9F9F7] border-0 rounded-xl text-base focus:ring-2 focus:ring-[#0B3D2E]/20"
                    data-testid="login-email-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#4A5D56] text-sm font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8FA39B]" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-12 pr-12 h-14 bg-[#F9F9F7] border-0 rounded-xl text-base focus:ring-2 focus:ring-[#0B3D2E]/20"
                    data-testid="login-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8FA39B] hover:text-[#0B3D2E] transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white rounded-full text-base font-brand transition-all"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </div>

          {/* Sign Up Link */}
          <p className="text-center text-[#4A5D56]">
            New to The TRIBE?{" "}
            <Link 
              to="/register" 
              className="text-[#0B3D2E] font-semibold hover:underline"
              data-testid="register-link"
            >
              Create Account
            </Link>
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="py-4 text-center">
        <p className="text-[10px] text-[#8FA39B] tracking-wide">
          Fitness is the vehicle, Hauora is the goal.
        </p>
      </div>
    </div>
  );
};

export default Login;
